import { useState, useEffect, createContext, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import {
  LayoutDashboard, Users, LogOut, Target, Heart,
  Settings, User, Star, Map, Repeat,
  Menu, X, PanelLeftClose, PanelLeft, ShieldCheck, BarChart3,
  Sparkles, CircleDot, HeartHandshake, ChevronDown, ChevronRight
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import { EvoluraLogo, EvoluraIcon } from "./EvoluraLogo";
import OnboardingDialog from "./OnboardingDialog";

const API = '/api';

const SidebarContext = createContext();
export const useSidebar = () => useContext(SidebarContext);

const AppLayout = ({ children }) => {
  const { user, setUser, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [gratitudePending, setGratitudePending] = useState(0);
  const [myUsername, setMyUsername] = useState(null);
  // "Myself" is now a collapsible group header (not a navigable page).
  // Auto-expand when on any of its children. Persisted across visits.
  const [myselfExpanded, setMyselfExpanded] = useState(() => {
    const saved = localStorage.getItem("sidebar_myself_expanded");
    return saved === null ? true : saved === "true";
  });
  const toggleMyselfExpanded = () => {
    setMyselfExpanded((v) => {
      const next = !v;
      try { localStorage.setItem("sidebar_myself_expanded", String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", collapsed);
  }, [collapsed]);

  // Apply the dark "mobile-aligned" theme to every page rendered inside
  // the AppLayout shell (i.e. authenticated app surfaces). Landing pages
  // mount their own body class and don't share this theme.
  useEffect(() => {
    document.body.classList.add("app-mode");
    return () => document.body.classList.remove("app-mode");
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lightweight poll for pending gratitude asks → sidebar badge
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const res = await fetch(`${API}/gratitude/wall`, { credentials: "include" });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        setGratitudePending((d.pending_asks || []).length);
      } catch {
        // silent — badge just won't show
      }
    };
    fetchPending();
    // Also fetch my @username (once per mount) for the sidebar identity line.
    (async () => {
      try {
        const r = await fetch(`${API}/profile`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setMyUsername(d.username || null);
        }
      } catch {
        // silent — sidebar will just not show the @handle line
      }
    })();
    const id = setInterval(fetchPending, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  const navItems = [
    // Admins land on their ops dashboard first — keeps moderation top-of-mind
    // and one tap away from any page. Non-admins don't see it at all.
    ...(user?.role === "admin"
      ? [{ type: "link", to: "/admin", icon: ShieldCheck, label: t("nav.adminDashboard"), testId: "nav-admin" }]
      : []),
    { type: "link", to: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard"), testId: "nav-dashboard" },
    // "Inner Circles" promoted to the top, directly below the Dashboard.
    { type: "link", to: "/network", icon: Users, label: t("nav.innerCircles"), testId: "nav-network" },
    { type: "section", label: t("nav.categories") },
    // "Myself" becomes a group toggle — not a link — that shows/hides the
    // 4 child tools below. Clicking it only expands/collapses.
    { type: "group", icon: User, label: t("nav.myself"), testId: "nav-myself" },
    { type: "sub", to: "/tools/swot", icon: Target, label: t("nav.personalSwot"), testId: "nav-swot" },
    { type: "sub", to: "/tools/values", icon: Heart, label: t("nav.coreValues"), testId: "nav-values" },
    { type: "sub", to: "/tools/wheel", icon: CircleDot, label: t("nav.wheelOfLife"), testId: "nav-wheel" },
    { type: "sub", to: "/tools/gratitude", icon: HeartHandshake, label: t("nav.gratitude"), testId: "nav-gratitude", badge: gratitudePending },
    { type: "coming_soon", icon: Star, label: t("nav.myDreams"), testId: "nav-my-dreams" },
    { type: "coming_soon", icon: Map, label: t("nav.myPlans"), testId: "nav-my-plans" },
    { type: "coming_soon", icon: Repeat, label: t("nav.myHabits"), testId: "nav-my-habits" },
  ];

  const SidebarContent = ({ mobile = false }) => (
    <div className={`flex flex-col flex-1 min-h-0 ${mobile ? "" : ""}`}>
      {/* Logo */}
      <div className={`flex items-center overflow-hidden ${collapsed && !mobile ? "justify-center px-3 py-3" : "justify-center px-4 py-5"}`}
        data-testid="sidebar-logo-header"
        style={{
          background: "linear-gradient(135deg, #f5f0ff 0%, #ede5ff 100%)",
          borderBottom: "1px solid #e2e8f0"
        }}
      >
        <Link
          to="/dashboard"
          className="relative z-10 flex items-center gap-2.5 rounded-lg hover:opacity-80 transition-opacity"
          data-testid="sidebar-logo-link"
          aria-label="Go to dashboard"
        >
          {collapsed && !mobile ? (
            <EvoluraIcon size={76} />
          ) : (
            <EvoluraLogo size={72} showText={true} variant="light" />
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item, i) => {
          if (item.type === "section") {
            if (collapsed && !mobile) return null;
            return (
              <div key={i} className="pt-4 pb-1 px-3">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{item.label}</p>
              </div>
            );
          }
          if (item.type === "group") {
            // "Myself" header — not a link; clicking toggles the 4 child
            // tools below (SWOT, Core Values, Wheel, Gratitude). The
            // "Soon" items (Dreams, Plans, Habits) are separate tracks
            // and remain visible even when the Myself group collapses.
            if (collapsed && !mobile) return null;
            const ChevIcon = myselfExpanded ? ChevronDown : ChevronRight;
            return (
              <button
                key={i}
                type="button"
                onClick={toggleMyselfExpanded}
                className="sidebar-nav-item w-full text-left"
                aria-expanded={myselfExpanded}
                data-testid={item.testId}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                <ChevIcon className="w-4 h-4 flex-shrink-0" style={{ color: "var(--color-text-placeholder)" }} />
              </button>
            );
          }
          if (item.type === "sub") {
            // When collapsed (desktop only), render sub items as icon-only
            // tooltipped rows so all tools are still reachable from the
            // narrow sidebar. The Myself group header is hidden but its
            // children stay visible as flat icons.
            if (collapsed && !mobile) {
              const subActiveCollapsed = isActive(item.to);
              return (
                <Link
                  key={i}
                  to={item.to}
                  className={`sidebar-nav-item ${subActiveCollapsed ? "active" : ""} justify-center px-0 relative`}
                  data-testid={item.testId}
                  title={item.label}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {item.badge > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-violet-500 rounded-full" />
                  )}
                </Link>
              );
            }
            // Hide child nav rows when the Myself group is collapsed
            if (!myselfExpanded) return null;
            const subActive = isActive(item.to);
            return (
              <Link key={i} to={item.to}
                className={`sidebar-nav-item ${subActive ? "active" : ""}`}
                style={{ paddingLeft: "2.25rem", fontSize: "13px" }}
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span>{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto bg-violet-500 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center"
                    data-testid={`${item.testId}-badge`}>
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </Link>
            );
          }
          if (item.type === "coming_soon") {
            return (
              <div
                key={i}
                className={`sidebar-nav-item cursor-default ${collapsed && !mobile ? "justify-center px-0" : ""}`}
                data-testid={item.testId}
                title={collapsed && !mobile ? `${item.label} — Coming soon` : undefined}
                style={{ opacity: 0.55 }}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" style={{ color: "var(--color-text-placeholder)" }} />
                {(!collapsed || mobile) && (
                  <>
                    <span style={{ color: "var(--color-text-placeholder)" }}>{item.label}</span>
                    <span className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{
                        color: "var(--color-text-interactive)",
                        background: "var(--color-brand-ghost)",
                        border: "0.5px solid var(--color-border-active)"
                      }}>
                      Soon
                    </span>
                  </>
                )}
              </div>
            );
          }
          const active = isActive(item.to);
          return (
            <Link
              key={i}
              to={item.to}
              className={`sidebar-nav-item ${active ? "active" : ""} ${collapsed && !mobile ? "justify-center px-0" : ""}`}
              data-testid={item.testId}
              title={collapsed && !mobile ? item.label : undefined}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {(!collapsed || mobile) && <span>{item.label}</span>}
              {(!collapsed || mobile) && item.badge > 0 && (
                <span className="ml-auto bg-violet-500 text-white text-xs rounded-full px-2 py-0.5">
                  {item.badge}
                </span>
              )}
              {collapsed && !mobile && item.badge > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-violet-500 rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-slate-200 p-3">
        {collapsed && !mobile ? (
          <div className="flex flex-col items-center gap-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-violet-100 text-violet-700 text-sm">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <Link to="/profile" className="sidebar-nav-item justify-center px-0" data-testid="nav-profile" title={t("nav.settings")}>
              <Settings className="w-5 h-5" />
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center p-2 text-slate-600 hover:text-slate-900"
              onClick={handleLogout}
              data-testid="logout-button"
              title={t("nav.signOut")}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3 px-2">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-violet-100 text-violet-700 text-sm">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                {myUsername && (
                  <Link
                    to={`/${myUsername}`}
                    className="text-xs text-slate-500 hover:text-violet-600 truncate block"
                    data-testid="sidebar-my-handle"
                    title="View my public profile"
                  >
                    @{myUsername}
                  </Link>
                )}
              </div>
            </div>
            <div className="space-y-0.5">
              <Link to="/profile" className="sidebar-nav-item" data-testid="nav-profile">
                <Settings className="w-5 h-5" />
                {t("nav.settings")}
              </Link>
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-600 hover:text-slate-900"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("nav.signOut")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <div className="min-h-screen bg-slate-50 flex">
        {/* Desktop Sidebar */}
        <aside
          className={`hidden md:flex flex-col border-r border-slate-200 bg-white h-screen sticky top-0 transition-all duration-200 ${
            collapsed ? "w-[68px]" : "w-60"
          }`}
          data-testid="app-sidebar"
        >
          <SidebarContent />
          {/* Collapse toggle */}
          <div className="border-t border-slate-200 p-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-center text-slate-400 hover:text-slate-600"
              onClick={() => setCollapsed(!collapsed)}
              data-testid="sidebar-collapse-toggle"
              title={collapsed ? "Expand sidebar" : "Minimize sidebar"}
            >
              {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </Button>
          </div>
        </aside>

        {/* Mobile Header */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 h-14">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 rounded-lg -ml-1 pl-1 pr-2 py-1 hover:bg-slate-50 transition-colors"
              data-testid="mobile-logo-link"
              aria-label="Go to dashboard"
            >
              <EvoluraLogo size={31} variant="light" />
            </Link>
            <div className="flex items-center gap-1">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                className="p-2"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-button"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-white flex flex-col">
              <SidebarContent mobile />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 pt-14 md:pt-0 relative">
          {/* Desktop Notification Bell — always visible top-right */}
          <div
            className="hidden md:block fixed top-4 z-30"
            style={{ right: "1.5rem" }}
            data-testid="desktop-notification-bell"
          >
            <NotificationBell />
          </div>
          {children}
        </main>
      </div>
      {/* First-login onboarding modal — fires when user.onboarded === false */}
      <OnboardingDialog
        user={user}
        onComplete={(prefs) => {
          // Snap the auth context so the modal closes immediately and the
          // language picker on Profile shows the new selections.
          setUser?.((prev) => prev ? {
            ...prev,
            onboarded: true,
            primary_language: prefs.primary_language || prev.primary_language,
            spoken_languages: prefs.spoken_languages || prev.spoken_languages,
          } : prev);
        }}
      />
    </SidebarContext.Provider>
  );
};

export default AppLayout;
